import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { To } from "react-router";

import { MapPanelScrollableArea } from "~/modules/map/components/MapPanel";

import { useReviewPlanUserGroupFiltering } from "../hooks/useReviewPlanUserGroupFiltering";
import { UserGroup } from "../hooks/useReviewPlanVisitGrouping";
import ReviewPlanSearchInput from "./ReviewPlanSearchInput";
import { ReviewPlanVisitsList } from "./ReviewPlanVisitsList";

type ReviewPlanPendingChangesProps = {
    visitHref: (plannerAreaId: string, plannerVisitId: string) => To;
    areaNameHref: (plannerAreaId: string) => To;
    userGroups: UserGroup[];
    selectedVisitIds: string[];
    isAllSelected: boolean;
    onToggleVisit: (plannerVisitId: string) => void;
    onToggleAll: () => void;
    onToggleUser: (userId: string | null) => void;
    isUserFullySelected: (userId: string | null) => boolean;
    isUserPartiallySelected: (userId: string | null) => boolean;
    getUserSelectedCount: (userId: string | null) => number;
    onConfirmSelection: () => void;
    onCancelSelection: () => void;
    loading: boolean;
};

export function ReviewPlanPendingChanges({
    visitHref,
    areaNameHref,
    userGroups,
    selectedVisitIds,
    isAllSelected,
    onToggleVisit,
    onToggleAll,
    onToggleUser,
    isUserFullySelected,
    isUserPartiallySelected,
    getUserSelectedCount,
    onConfirmSelection,
    onCancelSelection,
    loading,
}: ReviewPlanPendingChangesProps) {
    const { t } = useTranslation("plannerV2");
    const hasPendingChanges = userGroups.length > 0;

    const [searchTerm, setSearchTerm] = useState("");
    const filteredUserGroups = useReviewPlanUserGroupFiltering(userGroups, searchTerm);

    return (
        <>
            {hasPendingChanges ? (
                <>
                    <Box padding={2} paddingBlockEnd={0}>
                        <ReviewPlanSearchInput value={searchTerm} onChange={setSearchTerm} />
                    </Box>
                    <Stack
                        direction="row"
                        alignItems="center"
                        paddingInline={2}
                        sx={theme => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAllSelected}
                                    indeterminate={selectedVisitIds.length > 0 && !isAllSelected}
                                    onChange={onToggleAll}
                                />
                            }
                            label={t("reviewPlanPanel.selectAll")}
                        />
                    </Stack>
                    <MapPanelScrollableArea>
                        <Stack rowGap={2} sx={loading ? { opacity: 0.5, pointerEvents: "none" } : {}}>
                            {filteredUserGroups.length === 0 && (
                                <Typography padding={2}>{t("reviewPlanPanel.noSearchResults")}</Typography>
                            )}
                            <ReviewPlanVisitsList
                                userGroups={filteredUserGroups}
                                visitHref={visitHref}
                                areaNameHref={areaNameHref}
                                selectedVisitIds={selectedVisitIds}
                                onToggleVisit={onToggleVisit}
                                onToggleUser={onToggleUser}
                                isUserFullySelected={isUserFullySelected}
                                isUserPartiallySelected={isUserPartiallySelected}
                                getUserSelectedCount={getUserSelectedCount}
                            />
                        </Stack>
                    </MapPanelScrollableArea>
                    <Stack rowGap={1} sx={theme => ({ borderTop: `1px solid ${theme.palette.divider}` })} padding={2}>
                        <Stack direction="row" columnGap={1}>
                            <Button
                                variant="outlined"
                                sx={{ flexGrow: 1 }}
                                disabled={loading || selectedVisitIds.length === 0}
                                onClick={onCancelSelection}
                            >
                                {t("reviewPlanPanel.cancelSelected", { selectedCount: selectedVisitIds.length })}
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ flexGrow: 1 }}
                                disabled={loading || selectedVisitIds.length === 0}
                                onClick={onConfirmSelection}
                            >
                                {t("reviewPlanPanel.confirmSelected", { selectedCount: selectedVisitIds.length })}
                            </Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {t("reviewPlanPanel.notificationHint")}
                        </Typography>
                    </Stack>
                </>
            ) : (
                <Typography padding={2}>{t("reviewPlanPanel.noVisitsDrafted")}</Typography>
            )}
        </>
    );
}
